import { useTranslation } from 'react-i18next'

import type {
  ChartDataSource,
  DashboardDataSource,
  IntegrationDatabase,
  SummaryDataSource,
} from '@/api/types'
import { aggregationsForField } from '@/features/database/aggregations'
import { useFields } from '@/features/database/table/queries'
import { Label } from '@/shared/ui/label'
import {
  NativeSelect,
  NativeSelectOptGroup,
  NativeSelectOption,
} from '@/shared/ui/native-select'
import { Skeleton } from '@/shared/ui/skeleton'
import { aggregationFor, viewsFor } from '../configuration'
import { useDataSourceMutations, type DataSourceChanges } from '../data-source-mutations'
import { useIntegrations } from '../queries'

/**
 * What a widget counts.
 *
 * The two data sources a dashboard can hold ask the same four questions in the
 * same order -- which table, which view of it, which column, and what to work
 * out over that column -- so they are one form with one extra select for the
 * chart, which also needs to know what to group by. Upstream keeps two
 * components that differ by those few lines and have drifted in the wording of
 * everything else.
 *
 * **Each select saves the one thing it decides**, immediately. There is no save
 * button and nothing to abandon: a half-configured data source is a legitimate
 * state that the server already models, and it is what the panel is for.
 */
export function DataSourceForm({
  dashboardId,
  source,
}: {
  dashboardId: number
  source: DashboardDataSource
}) {
  const { t } = useTranslation()
  const integrations = useIntegrations(dashboardId)
  const { change, isPending } = useDataSourceMutations(dashboardId)

  const summary = source.type === 'local_baserow_aggregate_rows' ? source : null
  const chart = source.type === 'local_baserow_category_aggregate_rows' ? source : null
  if (summary === null && chart === null) return null

  const integration = integrations.data?.find((one) => one.id === source.integration_id)
  const databases: readonly IntegrationDatabase[] =
    integration !== undefined && integration.type === 'local_baserow'
      ? integration.context_data.databases
      : []
  const tableId = 'table_id' in source ? (source.table_id ?? null) : null

  const save = (changes: DataSourceChanges): void => {
    change(source.id, changes)
  }

  if (integrations.isPending) return <Skeleton className="h-32 w-full" />

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="source-table">{t('widget.table')}</Label>
        <NativeSelect
          id="source-table"
          className="w-full"
          value={tableId ?? ''}
          disabled={isPending}
          onChange={(event) => {
            // Sent alone. The view and the column belong to the table being
            // left, and the server clears both rather than being told to --
            // sending a column of the old table with the new one is a refusal.
            save({ table_id: Number(event.target.value) })
          }}
        >
          <NativeSelectOption value="" disabled>
            {t('widget.chooseTable')}
          </NativeSelectOption>
          {databases.map((database) => (
            <NativeSelectOptGroup key={database.id} label={database.name}>
              {database.tables.map((table) => (
                <NativeSelectOption key={table.id} value={table.id}>
                  {table.name}
                </NativeSelectOption>
              ))}
            </NativeSelectOptGroup>
          ))}
        </NativeSelect>
      </div>

      {tableId !== null && (
        <>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="source-view">{t('widget.view')}</Label>
            <NativeSelect
              id="source-view"
              className="w-full"
              value={('view_id' in source ? source.view_id : null) ?? ''}
              disabled={isPending}
              onChange={(event) => {
                const value = event.target.value
                save({ view_id: value === '' ? null : Number(value) })
              }}
            >
              {/* A data source with no view aggregates the whole table, which
                  is a real answer rather than a missing one. */}
              <NativeSelectOption value="">{t('widget.everyRow')}</NativeSelectOption>
              {viewsFor(databases, tableId).map((view) => (
                <NativeSelectOption key={view.id} value={view.id}>
                  {view.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>

          <TableColumns
            tableId={tableId}
            summary={summary}
            chart={chart}
            disabled={isPending}
            onSave={save}
          />
        </>
      )}
    </div>
  )
}

/**
 * The choices that need the table's schema, and so cannot be made until it has
 * arrived.
 *
 * A component of its own because the fields are a query about the chosen table:
 * before one is chosen there is nothing to ask for, and asking for table `0`
 * to keep the hook order the same is a request for a table that does not exist.
 */
function TableColumns({
  tableId,
  summary,
  chart,
  disabled,
  onSave,
}: {
  tableId: number
  summary: SummaryDataSource | null
  chart: ChartDataSource | null
  disabled: boolean
  onSave: (changes: DataSourceChanges) => void
}) {
  const { t } = useTranslation()
  const fields = useFields(tableId)

  if (fields.isPending) return <Skeleton className="h-20 w-full" />

  const all = fields.data ?? []
  const held = summary?.field_id ?? chart?.aggregation_field_id ?? null
  const aggregated = all.find((field) => field.id === held)
  const aggregations = aggregated === undefined ? [] : aggregationsForField(aggregated)

  /** The column being aggregated, and the aggregation that survives the change. */
  const chooseColumn = (fieldId: number): void => {
    const field = all.find((one) => one.id === fieldId)
    const aggregation = aggregationFor(
      field,
      summary?.aggregation_type ?? chart?.aggregation_type,
    )
    onSave(
      summary === null
        ? { aggregation_field_id: fieldId, aggregation_type: aggregation }
        : { field_id: fieldId, aggregation_type: aggregation },
    )
  }

  return (
    <>
      {chart !== null && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="source-group-by">{t('widget.groupBy')}</Label>
          <NativeSelect
            id="source-group-by"
            className="w-full"
            value={chart.group_by_field_id ?? ''}
            disabled={disabled}
            onChange={(event) => {
              onSave({ group_by_field_id: Number(event.target.value) })
            }}
          >
            <NativeSelectOption value="" disabled>
              {t('widget.chooseColumn')}
            </NativeSelectOption>
            {all.map((field) => (
              <NativeSelectOption key={field.id} value={field.id}>
                {field.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="source-field">{t('widget.column')}</Label>
        <NativeSelect
          id="source-field"
          className="w-full"
          value={held ?? ''}
          disabled={disabled}
          onChange={(event) => {
            chooseColumn(Number(event.target.value))
          }}
        >
          <NativeSelectOption value="" disabled>
            {t('widget.chooseColumn')}
          </NativeSelectOption>
          {all.map((field) => (
            <NativeSelectOption key={field.id} value={field.id}>
              {field.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="source-aggregation">{t('widget.aggregation')}</Label>
        <NativeSelect
          id="source-aggregation"
          className="w-full"
          value={summary?.aggregation_type ?? chart?.aggregation_type ?? ''}
          // Nothing to choose between until there is a column: which
          // aggregations mean anything is a property of what is being
          // aggregated, not of the widget.
          disabled={disabled || aggregations.length === 0}
          onChange={(event) => {
            onSave({ aggregation_type: event.target.value })
          }}
        >
          <NativeSelectOption value="" disabled>
            {t('widget.chooseAggregation')}
          </NativeSelectOption>
          {aggregations.map((name) => (
            <NativeSelectOption key={name} value={name}>
              {t(`aggregation.${name}`)}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>
    </>
  )
}
